import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { usePostHog } from '@posthog/react';
import { useAuth } from '../auth/AuthContext';
import { authFetch } from '../auth/token';

import './OrderDetail.css';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

const STEPS = [
    { key: 'placed', label: 'Placed' },
    { key: 'confirmed', label: 'Confirmed' },
    { key: 'shipped', label: 'Shipped' },
];

const OrderDetail = () => {
    const { orderNumber } = useParams();
    const [searchParams] = useSearchParams();
    const accessKey = searchParams.get('key');
    const posthog = usePostHog();
    const { email } = useAuth();
    const [order, setOrder] = useState(null);
    const [cert, setCert] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [trackingNumber, setTrackingNumber] = useState('');
    const [busy, setBusy] = useState(null); // 'confirm' | 'fulfill' | null
    const [notice, setNotice] = useState(null); // { kind: 'ok' | 'err', text }

    // Anonymous buyers arrive from checkout with ?key=…; signed-in buyers and sellers
    // are matched by email on the API.
    const fetchOrder = useCallback(async () => {
        try {
            const params = new URLSearchParams({ orderNumber });
            if (accessKey) params.set('accessKey', accessKey);
            const response = await authFetch(`${apiBaseUrl}/orders?${params}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.status === 401) {
                throw new Error('Sign in to view this order.');
            }
            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.statusText}`);
            }

            const result = await response.json();
            if (result.orders && result.orders.length > 0) {
                setOrder(result.orders[0]);
            } else {
                throw new Error('Order not found');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [orderNumber, accessKey]);

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchOrder();
    }, [fetchOrder]);

    // Card details for the graded card on this order (name, grade, set, image).
    const certNumber = order?.cert_number;
    useEffect(() => {
        if (!certNumber) return undefined;
        let cancelled = false;
        fetch(`${apiBaseUrl}/certs?certNumber=${encodeURIComponent(certNumber)}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((r) => { if (!cancelled) setCert(r?.certs?.[0] || null); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [certNumber]);

    const isSeller = !!email && !!order && email === order.seller_email;

    const postAction = async (kind, path, body, event) => {
        if (!isSeller) return;
        setBusy(kind);
        setNotice(null);
        try {
            const response = await authFetch(`${apiBaseUrl}/orders/${path}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ order_number: orderNumber, source: 'orders-page', ...body }),
            });
            if (!response.ok) {
                throw new Error(kind === 'confirm' ? 'Order confirmation failed' : 'Order fulfillment failed');
            }
            posthog.capture(event, { order_number: orderNumber, ...body });
            setNotice({ kind: 'ok', text: kind === 'confirm' ? 'Order confirmed.' : 'Marked as shipped.' });
            await fetchOrder();
        } catch (err) {
            console.error(`Order ${kind} error:`, err);
            posthog.captureException(err, { order_number: orderNumber });
            setNotice({ kind: 'err', text: kind === 'confirm' ? 'Couldn’t confirm the order. Try again.' : 'Couldn’t mark it shipped. Try again.' });
        } finally {
            setBusy(null);
        }
    };

    const handleConfirmOrder = () => postAction('confirm', 'confirmation', {}, 'order_confirmed');
    const handleFulfillOrder = () => {
        if (!trackingNumber.trim()) return;
        postAction('fulfill', 'fulfillment', { tracking_number: trackingNumber.trim() }, 'order_fulfilled');
    };

    if (loading) {
        return (
            <Layout>
                <div className="od-page">
                    <div className="od-grid" aria-busy="true">
                        <div className="od-media od-skeleton" />
                        <div className="od-card">
                            <div className="od-skeleton od-skel-line wide" />
                            <div className="od-skeleton od-skel-line" />
                            <div className="od-skeleton od-skel-block" />
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    if (error || !order) {
        return (
            <Layout>
                <div className="od-page">
                    <div className="od-card od-empty">
                        <h1>Order not found</h1>
                        <p>We couldn’t load order #{orderNumber}. Check the link and try again.</p>
                    </div>
                </div>
            </Layout>
        );
    }

    const { buyer_email, price, order_number } = order;
    const confirmed = order.order_confirmation_id != null;
    const shipped = order.order_fulfillment_id != null;
    const stepIndex = shipped ? 2 : confirmed ? 1 : 0;

    const imageUrl = cert?.cert_images?.front_image_url
        || `https://gradies.blob.core.windows.net/psa-images/${certNumber}/front-${certNumber}.jpg`;
    const card = cert?.card || {};
    const set = card.set || {};
    const name = card.name || cert?.name;
    const setLine = set.series && set.series !== set.name ? `${set.series}: ${set.name}` : set.name || cert?.set;

    return (
        <Layout>
            <div className="od-page">
                <div className="od-grid">
                    <div className="od-media">
                        <img src={imageUrl} alt={name ? `${name}, PSA cert ${certNumber}` : `PSA graded card ${certNumber}`} />
                    </div>

                    <div className="od-main">
                        <section className="od-card">
                            <div className="od-eyebrow">Order #{order_number}</div>
                            <h1 className="od-title">{name || `Cert #${certNumber}`}</h1>
                            <div className="od-chips">
                                {cert?.grade && <span className="od-chip dark">{cert.grade}</span>}
                                <a
                                    className="od-chip"
                                    href={`https://www.psacard.com/cert/${certNumber}/psa`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    PSA #{certNumber}
                                </a>
                            </div>
                            {setLine && <div className="od-sub">{setLine}{card.number ? ` #${card.number}` : ''}</div>}

                            <ol className="od-steps" aria-label="Order status">
                                {STEPS.map((s, i) => (
                                    <li
                                        key={s.key}
                                        className={i < stepIndex ? 'done' : i === stepIndex ? 'current' : ''}
                                        aria-current={i === stepIndex ? 'step' : undefined}
                                    >
                                        <span className="od-step-dot" aria-hidden="true">
                                            {i <= stepIndex ? '✓' : ''}
                                        </span>
                                        <span className="od-step-label">{s.label}</span>
                                    </li>
                                ))}
                            </ol>
                        </section>

                        <section className="od-card">
                            <dl className="od-summary">
                                <div><dt>Price</dt><dd>{money(price)}</dd></div>
                                <div className="od-total"><dt>Total</dt><dd>{money(price)}</dd></div>
                            </dl>
                            <dl className="od-facts">
                                <div><dt>Buyer</dt><dd>{buyer_email}</dd></div>
                            </dl>
                        </section>

                        {isSeller && (
                            <section className="od-card">
                                <h2 className="od-card-title">Seller actions</h2>
                                {notice && <div className={`od-notice ${notice.kind}`} role="status">{notice.text}</div>}

                                {!confirmed && (
                                    <button
                                        type="button"
                                        className="od-btn primary"
                                        onClick={handleConfirmOrder}
                                        disabled={busy !== null}
                                    >
                                        {busy === 'confirm' ? 'Confirming…' : 'Confirm order'}
                                    </button>
                                )}

                                {confirmed && !shipped && (
                                    <div className="od-ship">
                                        <input
                                            type="text"
                                            value={trackingNumber}
                                            onChange={(e) => setTrackingNumber(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleFulfillOrder()}
                                            placeholder="Tracking number"
                                            aria-label="Tracking number"
                                            disabled={busy !== null}
                                        />
                                        <button
                                            type="button"
                                            className="od-btn primary"
                                            onClick={handleFulfillOrder}
                                            disabled={busy !== null || !trackingNumber.trim()}
                                        >
                                            {busy === 'fulfill' ? 'Saving…' : 'Mark shipped'}
                                        </button>
                                    </div>
                                )}

                                {shipped && <div className="od-done">✓ Shipped — nothing left to do.</div>}
                            </section>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default OrderDetail;
